import { useState, useMemo, useEffect, useRef } from 'react';
import categories from '../data/categories.json';
import questions from '../data/questions.json';
import feedback from '../data/feedback.json';
import { stickyHide } from '../helpers/stickyHide';
import { Instructions } from './Instructions';

export default function Quiz() {
  const initScores = () => {
    let result = []
    for (let i = 0; i < questions.length; i++) {
      result.push(0)
    }
    return result
  }

  const [categoryScore, setCategoryScore] = useState(categories);
  const [scoreArr, setScoreArr] = useState(initScores());
  const [error, setError] = useState(null);
  const [errorIndex, setErrorIndex] = useState([]);
  const [isStickyVisible, setIsStickyVisible] = useState(true);

  // create react ref
  const stickyRef = useRef(null);

  // useEffect to listen for scroll event to hide element when element reaches the top of the element passed
  useEffect(() => {
    setIsStickyVisible(stickyHide(stickyRef.current, 0))
    window.addEventListener('scroll', () => {
      setIsStickyVisible(stickyHide(stickyRef.current, 0))
    });
    // set focus to first input
    document.getElementById('question-0').focus();

    // scroll to the top of the page
    window.scrollTo(0, 0);
  }, []);

  const handleAnswer = (event) => {
    const index = parseInt(event.target.name.replace('question-', ''), 10)
    const input = document.querySelector(`#question-${index}`)
    let inputValue = parseInt(input.value, 10)

    // validate value as number between 0 and 4
    if (input.value.length > 1) {
      input.value = input.value[0]
      inputValue = parseInt(input.value, 10)
    }
    if (isNaN(inputValue) || inputValue < 0 || inputValue > 3) {
      inputValue = 0
      setErrorIndex([...errorIndex, index])
      setError('Please enter a number between 0 and 3')
    } else {
      setErrorIndex(errorIndex.filter(i => i !== index))
      setError(null)
    }

    // update score
    updateScores(index, inputValue)
  }

  const updateScores = (index, value) => {
    const scoreCopy = [...scoreArr]
    scoreCopy[index] = value

    let newScores = { ...categories }
    for (let i = 0; i < scoreCopy.length; i++) {
      const category = questions[i].category
      newScores[category] += scoreCopy[i]
    }

    setCategoryScore(newScores)
    setScoreArr(scoreCopy)
  }

  // Save scores to html file
  const saveScoresHTML = () => {
    const scores = Object.keys(categoryScore).map(category => {
      return {
        category,
        score: categoryScore[category]
      }
    }
    )
    const html = `<!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta http-equiv="X-UA-Compatible" content="ie=edge">
      <title>Coaching Styles and the Five Elements Results</title>
      <style>
        table, th, td {
          border: 1px solid black;
        }
        table {
          width: 100%;
        }
        .container {
          max-width: 700px;
          margin: 0 auto;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <h1>Results</h1>
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            ${scores.map(score => `
              <tr>
                <td>${score.category}</td>
                <td>${score.score}</td> 
              </tr>
            `).join('')}
          </tbody>
        </table>
        <br />
        ${getHighestCategory.filter(category => category.show).map((category, index) => {
          return `<div key=${index} class="results">
                    <h3>Category</h3>
                    <p style="text-transform: capitalize;">${category.category}</p>
                    <h3>Type</h3>
                    <p style="text-transform: capitalize;">${category.type}</p>
                    <h3>Description</h3>
                    <p>${category.description}</p>
                    <hr />
                  </div>`
          }).join('<br/>')}
      </div>
    </body>
    </html>`
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'fivestyle-results.html'
    link.click()
  }

  const handleButtonIncrement = (event) => {
    const index = parseInt(event.target.name.replace('button-increment-', ''), 10)
    const input = document.querySelector(`#question-${index}`)
    if (input.value === '' || isNaN(parseInt(input.value, 10))) {
      input.value = 0
    } else if (input.value >= 0 && input.value < 3) {
      input.value = parseInt(input.value, 10) + 1
    } else {
      input.value = 3
    }
    setErrorIndex(errorIndex.filter(i => i !== index))
    setError(null)

    // update score
    updateScores(index, parseInt(input.value, 10))
  }

  const handleButtonDecrement = (event) => {
    const index = parseInt(event.target.name.replace('button-decrement-', ''), 10)
    const input = document.querySelector(`#question-${index}`)
    if (input.value === '' || isNaN(parseInt(input.value, 10))) {
      input.value = 0
    } else if (input.value > 0 && input.value < 4) {
      input.value = parseInt(input.value, 10) - 1
    } else {
      input.value = 0
    }
    setErrorIndex(errorIndex.filter(i => i !== index))
    setError(null)

    // update score
    updateScores(index, parseInt(input.value, 10))
  }

  // Return highest scoring category
  const getHighestCategory = useMemo(() => {
    // build object of categories and feedback
    const categoryData = Object.keys(categoryScore).map(category => {
      return {
        category,
        type: feedback[category].type,
        description: feedback[category].description,
        show: false
      }
    })
    let highestScore = 0
    let sortedCategories = Object.keys(categoryScore).sort((a, b) => categoryScore[b] - categoryScore[a])
    for (let i = 0; i < sortedCategories.length; i++) {
      const category = sortedCategories[i]
      const score = categoryScore[category]
      if (score >= highestScore) {
        highestScore = score
        categoryData[categoryData.findIndex(c => c.category === category)].show = true
      } else {
        categoryData[categoryData.findIndex(c => c.category === category)].show = false
      }
    }
    return categoryData
  }, [categoryScore])

  return (
    <>
      <Instructions />
      {isStickyVisible && (<div className='header sticky-top'>
        <p>Enter a number between 0 and 3 for each</p>
        <p>0&nbsp;=&nbsp;<em>not</em> | 1&nbsp;=&nbsp;<em>somewhat</em> | 2&nbsp;=&nbsp;<em>strongly</em> | 3&nbsp;=&nbsp;<em>always</em></p>
        {error && (<p style={{ backgroundColor: "#B12028" }}>{error}</p>)}
      </div>)}
      <ul className='questions'>
        {questions.map((question, index) => (
          <li key={index} className={errorIndex.includes(index) ? 'error' : null}>
            <div className='input-controls'>
              <button
                name={`button-decrement-${index}`}
                aria-label={`decrease question ${index} by 1`}
                onClick={handleButtonDecrement}
                tabIndex='-1'
                >
                  -
              </button>
              <input
                type="number"
                name={`question-${index}`}
                id={`question-${index}`}
                data-category={question.category}
                onChange={handleAnswer}
                maxLength={1}
                min={0}
                max={3}
              />
              <button
                name={`button-increment-${index}`}
                aria-label={`increase question ${index} by 1`}
                onClick={handleButtonIncrement}
                tabIndex='-1'
              >+</button>
            </div>
            <div>
              <label htmlFor={`question-${index}`}>
                {question.question}
              </label>
            </div>
          </li>
        ))}
      </ul>
      {/* show each category score */}
      <ul className='scores sticky-bottom' ref={stickyRef}>
        {Object.keys(categoryScore).map((category, index) => (
          <li key={index} className={category}>
            <p>{category}</p>
            <p>{categoryScore[category]}</p>
          </li>
        ))}
      </ul>
      {/* show highest scoring category result */}
      <div>
        {/* display category data with show equal to true */}
        <div className='sticky-top'>
          <h2>Result{(getHighestCategory.filter(category => category.show).length > 1) && ('s')}</h2>
          <hr />
        </div>
        {getHighestCategory.filter(category => category.show).map((category, index) => (
          <div key={index} className={'results'}>
            <h3 className='results-heading'>Category</h3>
            <p className='results-short'>{category.category}</p>
            <h3 className='results-heading'>Type</h3>
            <p className='results-short'>{category.type}</p>
            <h3 className='results-heading'>Description</h3>
            <p className='results-description'>{category.description}</p>
          </div>
        ))}
      </div>
      {/* save scores to text file */}
      <div className='container mob-text-center'>
        <button
          className='btn btn-primary'
          aria-label='save results to HTML file'
          onClick={saveScoresHTML}
        >
          Save Results
        </button>
      </div>
    </>
  );
}